import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";

import {
  Button,
  Card,
  Input,
  SectionHeader,
  Tag,
  Textarea,
} from "../../../../design-system";
import {
  InstagramHealthVerdict,
  InstagramSessionHealth,
  InstagramSessionHealthAvailable,
} from "../../../../types/OutreachMetrics";
import {
  getInstagramSessionHealth,
  recordInstagramSeedCheck,
} from "../../../../database/outreach/outreachApi";

const HealthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-3);

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const HealthCell = styled.div`
  padding: var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
`;

const CellHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
`;

const CellTitle = styled.span`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const CellValue = styled.div`
  margin-top: var(--space-3);
  font-family: var(--font-display);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const CellHint = styled.p`
  margin: var(--space-2) 0 0;
  font-size: var(--font-size-xs);
  line-height: 1.5;
  color: var(--color-text-tertiary);
`;

const Note = styled.p`
  margin: var(--space-4) 0 0;
  font-size: var(--font-size-sm);
  line-height: 1.55;
  color: var(--color-text-secondary);
`;

const ErrorText = styled.p`
  margin: var(--space-3) 0 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: minmax(150px, 1fr) minmax(150px, 0.8fr) minmax(
      170px,
      1fr
    );
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border);

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
`;

const Select = styled.select`
  height: 36px;
  padding: 0 var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
`;

const FormWide = styled.div`
  grid-column: 1 / -1;
`;

const FormActions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
`;

const verdictVariant = (
  verdict: InstagramHealthVerdict
): "success" | "warning" | "danger" | "neutral" =>
  verdict === "green"
    ? "success"
    : verdict === "yellow"
    ? "warning"
    : verdict === "red"
    ? "danger"
    : "neutral";

const verdictLabel = (verdict: InstagramHealthVerdict) =>
  verdict === "green"
    ? "Healthy"
    : verdict === "yellow"
    ? "Watch"
    : verdict === "red"
    ? "Stop"
    : "Unknown";

const fmtRate = (value: number | null) =>
  value == null ? "—" : `${Math.round(value * 100)}%`;

const runtimeLabel = (health: InstagramSessionHealthAvailable) => {
  if (health.runtime.armed) return "Armed";
  if (!health.runtime.browserAdapterReady) return "Adapter gated";
  if (!health.runtime.workerEnabled) return "Disabled";
  if (health.runtime.dailyCap <= 0) return "Cap is zero";
  return "Not armed";
};

const engagementLabel = (health: InstagramSessionHealthAvailable) => {
  if (!health.runtime.engagementAdapterReady) return "adapter gated";
  if (!health.runtime.engagementEnabled) return "disabled";
  if (
    (health.runtime.followDailyCap || 0) <= 0 &&
    (health.runtime.commentDailyCap || 0) <= 0
  ) {
    return "caps are zero";
  }
  return "armed";
};

const InstagramAccountHealth: React.FC<{ accountId: string }> = ({
  accountId,
}) => {
  const [health, setHealth] = useState<InstagramSessionHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recipientAlias, setRecipientAlias] = useState("");
  const [relationship, setRelationship] = useState<
    "follower" | "non_follower"
  >("non_follower");
  const [placement, setPlacement] = useState<
    | "primary"
    | "requests"
    | "hidden_requests"
    | "spam"
    | "not_received"
    | "unknown"
  >("requests");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setHealth(await getInstagramSessionHealth(accountId));
    } catch (e: any) {
      setHealth(null);
      setError(e?.message || "Could not load Instagram account health.");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await recordInstagramSeedCheck({
        accountId,
        recipientAlias,
        relationship,
        placement,
        observedAt: new Date().toISOString(),
        note: note.trim() || undefined,
      });
      setHealth(result.health);
      setRecipientAlias("");
      setNote("");
      setShowForm(false);
    } catch (e: any) {
      setError(e?.message || "Could not save the seed check.");
    } finally {
      setSaving(false);
    }
  };

  if (!loading && health?.available === false) return null;

  if (!health) {
    if (!error) return null;
    return (
      <Card padding="lg">
        <SectionHeader
          title="Instagram account health"
          actions={
            <Button variant="ghost" size="sm" onClick={load}>
              Retry
            </Button>
          }
        />
        <ErrorText>{error}</ErrorText>
      </Card>
    );
  }

  if (!health.available) return null;

  return (
    <Card padding="lg">
      <SectionHeader
        title="Instagram account health"
        description="Operational safety and recipient-observed inbox placement for the founder session."
        actions={
          <>
            <Tag variant={verdictVariant(health.overall)} size="sm" withDot>
              Overall: {verdictLabel(health.overall)}
            </Tag>
            <Button variant="ghost" size="sm" onClick={load} isLoading={loading}>
              Refresh
            </Button>
          </>
        }
      />

      <HealthGrid>
        <HealthCell>
          <CellHead>
            <CellTitle>Operational</CellTitle>
            <Tag
              variant={verdictVariant(health.operational.verdict)}
              size="sm"
            >
              {verdictLabel(health.operational.verdict)}
            </Tag>
          </CellHead>
          <CellValue>{health.session.status.replace(/_/g, " ")}</CellValue>
          <CellHint>
            {runtimeLabel(health)} · {health.quota.firstContactAttempts}/
            {health.runtime.dailyCap} attempts today ·{" "}
            {health.operational.ambiguous} ambiguous
            <br />
            Engagement {engagementLabel(health)} · follows{" "}
            {health.quota.followAttempts || 0}/
            {health.runtime.followDailyCap || 0} · comments{" "}
            {health.quota.commentAttempts || 0}/
            {health.runtime.commentDailyCap || 0}
          </CellHint>
        </HealthCell>

        <HealthCell>
          <CellHead>
            <CellTitle>Inbox placement</CellTitle>
            <Tag
              variant={verdictVariant(health.inboxPlacement.verdict)}
              size="sm"
            >
              {verdictLabel(health.inboxPlacement.verdict)}
            </Tag>
          </CellHead>
          <CellValue>
            {health.inboxPlacement.recentSeedChecks} recent checks
          </CellValue>
          <CellHint>
            {health.inboxPlacement.concerningSeedChecks} concerning ·{" "}
            {health.inboxPlacement.confidence} confidence. Requests is normal
            for non-followers.
          </CellHint>
        </HealthCell>

        <HealthCell>
          <CellHead>
            <CellTitle>Recipient outcomes</CellTitle>
            <Tag
              variant={verdictVariant(health.outcomes.verdict)}
              size="sm"
            >
              {verdictLabel(health.outcomes.verdict)}
            </Tag>
          </CellHead>
          <CellValue>
            {fmtRate(health.outcomes.replyRate)} reply ·{" "}
            {fmtRate(health.outcomes.seenRate)} seen
          </CellValue>
          <CellHint>
            {health.outcomes.replies} replies · {health.outcomes.seen} seen of{" "}
            {health.outcomes.sent} sent. Outcomes do not prove spam placement.
          </CellHint>
        </HealthCell>
      </HealthGrid>

      <Note>
        Instagram exposes no reliable sender-side spam flag. Direct evidence
        comes from manually checking controlled seed recipients. The verdict
        remains unknown until at least three recent non-unknown observations.
        {health.session.lastActionAt
          ? ` Last action ${moment(health.session.lastActionAt).fromNow()}.`
          : ""}
      </Note>

      {!showForm ? (
        <div style={{ marginTop: "var(--space-4)" }}>
          <Button variant="secondary" size="sm" onClick={() => setShowForm(true)}>
            Record seed inbox check
          </Button>
        </div>
      ) : (
        <Form onSubmit={submit}>
          <Input
            label="Seed alias"
            value={recipientAlias}
            onChange={(e) => setRecipientAlias(e.target.value)}
            placeholder="e.g. seed-nonfollower-1"
            helperText="Use an alias, never the real handle."
            maxLength={80}
            required
            fullWidth
          />
          <Field>
            Relationship
            <Select
              value={relationship}
              onChange={(e) =>
                setRelationship(
                  e.target.value as "follower" | "non_follower"
                )
              }
            >
              <option value="non_follower">Non-follower</option>
              <option value="follower">Follower</option>
            </Select>
          </Field>
          <Field>
            Observed placement
            <Select
              value={placement}
              onChange={(e) => setPlacement(e.target.value as typeof placement)}
            >
              <option value="requests">Requests</option>
              <option value="primary">Primary inbox</option>
              <option value="hidden_requests">Hidden requests</option>
              <option value="spam">Spam</option>
              <option value="not_received">Not received</option>
              <option value="unknown">Could not determine</option>
            </Select>
          </Field>
          <FormWide>
            <Textarea
              label="Optional note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              fullWidth
            />
          </FormWide>
          <FormActions>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              isLoading={saving}
            >
              Save observation
            </Button>
          </FormActions>
        </Form>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </Card>
  );
};

export default InstagramAccountHealth;
